import axios from 'axios';
import { Contact } from './api.contacts';
import { notifySetupStateChanged } from './utils/setupStep';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

export interface Apartment {
  id?: number;
  name: string;
  internalName?: string;
  displayName: string;
  address: string;
  capacity: number;
  publicCheckinWindowDays?: number;
  description: string;
  isActive: boolean;
  assignedContact?: Contact;

  // Public guest check-in URL key
  publicCheckinKey?: string;

  // Apartment-owned Ubyport settings used for sending guest data.
  ubyportUsername?: string;
  ubyportPassword?: string;
  hasUbyportPassword?: boolean;
  idub?: string;
  ubyportShortName?: string;  // uMark in Ubyport SOAP request (max 10 chars)
  autoSendToUbyport?: boolean;
  /** Max foreign guests per reservation sent to Ubyport automatically (1-50); null/absent = all guests (#206). */
  ubyportAutoSendMaxGuests?: number | null;
  /** Days after check-in (0-3) before automatic sending starts; null/absent = check-in day (#206). */
  ubyportAutoSendDelayDays?: number | null;
  hostFullName?: string;      // uName in Ubyport SOAP request (max 30 chars)
  hostPhone?: string;         // uCont in Ubyport SOAP request (max 50 chars)

  // Contact Information
  contactFullName?: string;
  contactEmail?: string;
  contactPhone?: string;

  // Which contact to show in guest form footer (manager, host, or contact)
  guestFooterContactType?: string;

  // City tax rates (amount per guest per night)
  cityTaxAdult?: number;
  cityTaxChild?: number;
  cityTaxDisabled?: number;

  // City tax payer info
  cityTaxContactId?: number;
  cityTaxContact?: { id?: number };
  cityTaxBankAccount?: string;
  cityTaxVariableSymbol?: string;

  // City tax mailbox
  cityTaxMailboxId?: number;
  cityTaxMailbox?: { id?: number };
  cityTaxMailboxOtherName?: string;
  cityTaxMailboxOtherDataboxId?: string;
  cityTaxMailboxOtherAddress?: string;

  // City tax online collection (guest pays via Stripe before Check-in Guide unlocks).
  cityTaxCollectionEnabled?: boolean;
  // Which of the manager's connected Stripe accounts (a type='STRIPE' payment method) receives each
  // kind of payment — a manager can have several, see api.settings.ts. Picked per purpose since
  // issue #114; each may be the same account or a different one.
  //
  // `paymentMethodId` / `paymentMethod` is the CITY TAX account (what the field always meant).
  paymentMethodId?: number;
  paymentMethod?: { id?: number };
  // Extras sold alongside the stay: early check-in, late check-out, luggage.
  upsellPaymentMethodId?: number;
  upsellPaymentMethod?: { id?: number };
  // The deposit card and hold.
  depositPaymentMethodId?: number;
  depositPaymentMethod?: { id?: number };
  // Response only: the effective account per purpose (an apartment that was never split reports its
  // one shared account for all three) and whether it has been split. A save always sends the three
  // `*PaymentMethod` objects above — `{ id: 0 }` meaning none — see utils/apartmentPaymentMethods.ts.
  cityTaxPaymentMethodId?: number;
  paymentMethodsSplit?: boolean;

  // TTLock smart lock
  ttlockEnabled?: boolean;
  ttlockLockId?: number | null;
  checkinTime?: string;
  checkoutTime?: string;
  collisionStrategy?: 'STRICT' | 'FIRST_WINS' | 'ALLOW' | string;
  ttlockPinsClearedCount?: number;
  // Lets guests scan their passport/ID (client-side MRZ OCR) to prefill the check-in form.
  documentScanEnabled?: boolean;
    /**
     * Whether every calendar source (channel manager or iCal) linked to this apartment last synced
     * cleanly. Undefined/null when no source is configured at all — nothing is broken, there is
     * simply nothing to report on.
     */
    calendarSyncOk?: boolean | null;
}

export const getApartments = async (token: string) => {
  const res = await axios.get(`${API_BASE}/apartments`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const getApartmentById = async (id: number, token: string) => {
  const res = await axios.get(`${API_BASE}/apartments/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const createApartment = async (apartment: Apartment, token: string) => {
  const res = await axios.post(`${API_BASE}/apartments`, apartment, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  notifySetupStateChanged();
  return res.data;
};

export const updateApartment = async (id: number, apartment: Apartment, token: string) => {
  const res = await axios.put(`${API_BASE}/apartments/${id}`, apartment, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export interface ApartmentDeleteSummary {
  reservationCount: number;
  ubyportSubmittedCount: number;
  dorucenkaCount: number;
  calendarCount: number;
}

export const getApartmentDeleteSummary = async (id: number, token: string): Promise<ApartmentDeleteSummary> => {
  const res = await axios.get(`${API_BASE}/apartments/${id}/delete-summary`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const deleteApartment = async (id: number, token: string) => {
  await axios.delete(`${API_BASE}/apartments/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  notifySetupStateChanged();
};

export const clearTtlockPins = async (id: number, token: string): Promise<{ clearedCount: number }> => {
  const res = await axios.post(`${API_BASE}/apartments/${id}/ttlock/clear-pins`, {}, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const runTtlockGenerationJob = async (id: number, token: string): Promise<{ generatedCount: number }> => {
  const res = await axios.post(`${API_BASE}/apartments/${id}/ttlock/run-generation-job`, {}, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export type UbyportCheckStatus =
  | 'OK' | 'INVALID_CREDENTIALS' | 'INCOMPLETE' | 'TIMEOUT' | 'UNREACHABLE'
  | 'PROVIDER_ERROR' | 'NOT_CONFIGURED' | 'BUSY' | 'RATE_LIMITED';

export interface UbyportCredentialCheck {
  status: UbyportCheckStatus;
  ok: boolean;
  environment?: 'TESTING' | 'PRODUCTION' | null;
  testedUnsavedValues: boolean;
  usedSavedPassword: boolean;
  retryAfterSeconds?: number | null;
}

/**
 * Asks Ubyport whether these credentials get in. Nothing is saved and no guest is sent. Leave
 * `username` blank, or `password` blank, to test the stored one; the stored password never
 * reaches the browser, so the form can only say "keep it". 429 is an answer here (BUSY or
 * RATE_LIMITED), not an exception.
 */
export const testUbyportCredentials = async (
  id: number,
  credentials: { username?: string; password?: string },
  token: string,
): Promise<UbyportCredentialCheck> => {
  const res = await axios.post(`${API_BASE}/apartments/${id}/ubyport/test-credentials`, credentials, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
    validateStatus: status => status === 200 || status === 429,
  });
  return res.data;
};
